import axios, { AxiosRequestConfig } from 'axios';
import langFallback from '@data/label';
import { IGlobalOrderDetails } from '@interfaces/myPurchasesInterfaces';
import URLS from '@utils/urlList';
import { LOGON_ID_STORE } from '@utils/const';
import moment from 'moment';

let lang = {};

/* LABELS */
export const getLabels = async langUrl => {
    if (langUrl) {
        await axios
            .get(langUrl)
            .then(function (response) {
                lang = response.data;
            })
            .catch(function (error) {
                console.error('>>> Get labels => error status:', error.status, ', error: ', error);
            });
    }
};

export const getLabelFallback = label => {
    if (lang && lang[label]) {
        return lang[label];
    }

    return langFallback[label] ?? "";
};

/* DEFINE DEVICE */
export const isMobile = {
    Android: () => {
        return navigator.userAgent.match(/Android/i);
    },
    BlackBerry: () => {
        return navigator.userAgent.match(/BlackBerry/i);
    },
    iOS: () => {
        return (
            /iPad|iPhone|iPod/.test(navigator.userAgent) ||
            (/Macintosh/.test(navigator.userAgent) && navigator.maxTouchPoints > 1)
        );
    },
    Opera: () => {
        return navigator.userAgent.match(/Opera Mini/i);
    },
    Windows: () => {
        return navigator.userAgent.match(/IEMobile/i) || navigator.userAgent.match(/WPDesktop/i);
    },
    any: () => {
        return isMobile.Android() ||
            isMobile.BlackBerry() ||
            isMobile.iOS() ||
            isMobile.Opera() ||
            isMobile.Windows()
            ? true
            : false;
    },
};

/* STORE UTILS */
export const updateObject = (oldObject, updatedProperties) => {
    return {
        ...oldObject,
        ...updatedProperties,
    };
};

/*SERVICE UTILS*/
/**
 * Read the content of the file given in input and return a Promise object of its content
 * @param string file
 * @returns {Promise<any>} Promise
 */
export const readFile = async (file: string): Promise<any> => {
    const data = await fetch(file);
    const dataJson = await data.json();
    return new Promise((resolve, reject) => {
        if (dataJson) resolve({ data: dataJson });
        reject();
    });
};

export const convertDate = date => {
    const dateSplitter = date.split('/');

    const returnDate = dateSplitter[2] + '-' + dateSplitter[0] + '-' + dateSplitter[1];

    return returnDate;
};

export function sleep(ms: number): (p: Promise<any>) => any {
    return function (x: any) {
        return new Promise(resolve => setTimeout(() => resolve(x), ms));
    };
}
export async function getMockedFile(file: string) {
    return await readFile(file).then(sleep(1000)); // add a delay to simulate fetching
}

/* getOrderedRxcReviewObject - this function is used to get and order by 'sequence', the properties of the object shown in 'Your Lenses' section */
export const getOrderedRxcReviewObject = xitem_rxcReviewObject => {
    const rxcReviewObject = JSON.parse(xitem_rxcReviewObject);
    if (rxcReviewObject === null) return null;
    return Object.keys(rxcReviewObject)
        .map(function (key) {
            return rxcReviewObject[key];
        })
        .sort((a, b) => (a.sequence > b.sequence ? 1 : -1));
};

export const isValidJson = str => {
    try {
        JSON.parse(str);
    } catch (e) {
        return false;
    }
    return true;
};

/*COMMON UTILS*/
/*repace {str} in a string with JSX element*/
export const replaceStringWithElement = (
    text: string,
    str: string,
    element: JSX.Element
): (JSX.Element | string)[] => {
    const index = 1;
    const textArray: (JSX.Element | string)[] = text.split(`{${str}}`);
    return [...textArray.slice(0, index), element, ...textArray.slice(index)];
};

/**
 * Capitalize the first letter of a string
 *
 * @param {(string)} str
 * @return {*}  {string}
 */

export const capitalizeFirstLetter = (str: string): string =>
    str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();

/**
 * The function below returns the a substring of the entire phone number
 *
 * @param {(string)} phoneNumber
 * @param {(number)} startIndex
 * @param {(number)} endIndex
 * @return {*}  {string}
 */

export const getPartialPhoneNumber = (
    phoneNumber: string,
    startIndex: number,
    endIndex: number
): string => {
    const onlyNumbers = phoneNumber.replace(/\D/g, '');
    return onlyNumbers.substring(startIndex, endIndex);
};

/**
 * The function below returns the a substring of the entire phone number
 *
 * @param {(T)} formData the form data including form status

 * @return {boolean}  true if all form fields have values and not errors otherwise false
 */

export function isValidForm<T>(formData: T): boolean {
    return Object.keys(formData).some(function (key) {
        return formData[key].error || !formData[key].value;
    });
}

/**
 * The function below returns  if order is closed 
 * 
 * @param {IGlobalOrderDetails} order the order to control
 * 
 * @return {boolean} true if order is closed
 */

export function isOrderClosed(order: IGlobalOrderDetails): boolean {
    let value = true;
    order.orderItems.map(orderItem => {
        if (orderItem.orderStatus == 'S' || orderItem.orderStatus == 'X' || orderItem.orderStatus == 'D' || orderItem.orderStatus == 'U') {
            value = false;
        }
    })
    return value;
}

/**
 * The function below returns product details  
 **/
export async function getProductDetails(productId): Promise<any> {
    const onlineProductSkuDetailsUrl =
        URLS.GET_PRODUCT_DETAILS.replace(
            '{storeId}',
            LOGON_ID_STORE.split('|')[0]
        ).replace('{productId}', productId);
    const config: AxiosRequestConfig = {
        method: 'GET',
        url: onlineProductSkuDetailsUrl,
        headers: { 'Content-Type': 'application/json' },
    };

    return axios(config);
}

// MY PRESCRIPTIONS IMPORT

export const base64IsImage = (base64: string) => base64.match(/\/(png|jpg|jpeg|gif|bmp|tiff);/);

export const base64FileToImage = (base64: string) => base64.replace('data:@file', 'data:image');

export const clearPrescriptionFileName = (fileName: string, prescriptionId?: string) => {
    let tempFileName = fileName.replace(/ /g, '');
    tempFileName = !!prescriptionId
        ? tempFileName.replace('_' + prescriptionId + '_RX', '')
        : tempFileName;
    return tempFileName;
};

export const getFormattedDate = (dateStr: string) => {
    if (!!dateStr) {
        return moment(dateStr).format('MM/DD/YYYY');
    } else {
        return '';
    }
};

export const isToday = (dateStr: string) => {
    return !!dateStr && moment(dateStr).isSame(moment(), 'day');
};

export const isYesterday = (dateStr: string) => {
    return !!dateStr && moment(dateStr).isSame(moment().subtract(1, 'day'), 'day');
};

export const calculateNumberBox = (rightNumberBox, leftNumberBox) => {
    let result = 0;
    if (!!rightNumberBox && !isNaN(rightNumberBox)) {
        result += parseInt(rightNumberBox.toString());
    }
    if (!!leftNumberBox && !isNaN(leftNumberBox)) {
        result += parseInt(leftNumberBox.toString());
    }
    return result;
};

export const getSimplePrescriptionValuesArray = (
    start: number,
    end: number,
    incrementValue: number,
    noValueSymbol?: string,
    toFixed?: number,
    values?: string[],
    isFromContact?: boolean
) => {
    let valuesArray = [];
    if (noValueSymbol) {
        valuesArray.push({ name: noValueSymbol, value: '', default: true });
    }

    if (values) {
        //it's the non-numeric baseVert and baseHor fields
        if (!!isFromContact && !!Number(values[0])) {
            values.map(val => {
                valuesArray.push({ name: Number(val).toFixed(toFixed), value: Number(val).toFixed(toFixed), default: false });
            });
        } else {
            values.map(val => {
                valuesArray.push({ name: val, value: val, default: false });
            });
        }
    } else {
        for (let i = start; i <= end; i += incrementValue) {
            valuesArray.push({
                name: toFixed ? Number(i).toFixed(toFixed) : i,
                value: i,
                default: false,
            });
        }
    }

    return valuesArray;
};

export default {
    getPrismTableColumns: prescriptionDefaults => {
        return (verticalName, horizontalName, baseDirectionName) => [
            {
                id: 'vertical',
                type: 'vertical',
                placeholder: '-',
                required: true,
                name: verticalName,
                min: prescriptionDefaults.prismVertMin,
                max: prescriptionDefaults.prismVertMax,
                res: prescriptionDefaults.prismVertRes,
                rows: [
                    {
                        id: 'right_eye_vertical',
                    },
                    {
                        id: 'left_eye_vertical',
                    },
                ],
            },
            {
                id: 'baseDirectionVert',
                type: 'baseDirectionVert',
                placeholder: '-',
                required: true,
                name: baseDirectionName,
                values: prescriptionDefaults.prismBaseVert,
                rows: [
                    {
                        id: 'right_eye_baseDirVert',
                    },
                    {
                        id: 'left_eye_baseDirVert',
                    },
                ],
            },
            {
                id: 'horizontal',
                type: 'horizontal',
                placeholder: '-',
                required: true,
                name: horizontalName,
                min: prescriptionDefaults.prismHorMin,
                max: prescriptionDefaults.prismHorMax,
                res: prescriptionDefaults.prismHorRes,
                rows: [
                    {
                        id: 'right_eye_horizontal',
                    },
                    {
                        id: 'left_eye_horizontal',
                    },
                ],
            },
            {
                id: 'baseDirectionHor',
                type: 'baseDirectionHor',
                placeholder: '-',
                required: true,
                name: baseDirectionName,
                values: prescriptionDefaults.prismBaseHor,
                rows: [
                    {
                        id: 'right_eye_baseDirHor',
                    },
                    {
                        id: 'left_eye_baseDirHor',
                    },
                ],
            },
        ];
    },
};

export const getFileFromBase64 = async (base64: string, fileName: string): Promise<File> => {
    const fileType = base64.match(/^data:(.+);base64/)?.[1];
    const res: Response = await fetch(base64);
    const blob: Blob = await res.blob();

    return new File([blob], fileName, { type: fileType });
};

export const getFileDataFromBase64 = (base64: string) => {
    return base64.substring(base64.indexOf(',') + 1);
};

export const isValidNameOnlyLetters = value => {
    return /^[A-Za-z ]*$/.test(value) && value.length < 151;
};

export const getFormattedNumber = (value: string) => {
    if (!!value) {
        return value.replace(/(\d{3})(\d{3})(\d{4})/, '($1) $2-$3');
    } else {
        return value;
    }
};

export const roundNumber = number => {
    return Math.round(number * 10) / 10;
};

export const isCLAttributePresent = (item, position, attribute) => {
    let isPresent = !!item && !!item[position] && !!item[position][attribute] && item[position][attribute] != "0" 
            && item[position][attribute] != undefined && item[position][attribute] != '' 
            && item[position][attribute] != 'Clear' && item[position][attribute] != "0.0"; 
    return isPresent
};

export const isCLPosAttributePresent = (item) => {
    let isPresent = !!item && item != "0" 
            && item != undefined && item !='' 
            && item !='Clear' && item != "0.0"; 
    return isPresent;
};

export const getFormattedDateHours = (date: Date) => {
    return (
        date.getFullYear() +
        '-' +
        (1 + date.getMonth()).toString().padStart(2, '0') +
        '-' +
        date.getDate().toString().padStart(2, '0') +
        ' ' +
        date.getHours().toString().padStart(2, '0') +
        ':' +
        date.getMinutes().toString().padStart(2, '0') +
        ':' +
        date.getSeconds().toString().padStart(2, '0') +
        '.' +
        date.getMilliseconds().toString().padStart(3, '0')
    );
};

export const isValidName = value => {
    return /^[A-Za-z0-9 ]*$/.test(value) && value.length < 151;
};

export const getThreeYearsBack = () => {
    const today = new Date();

    const currentYear = today.getFullYear();

    const yearThreeYearsAgo = currentYear - 3;

    const threeYearsAgo = new Date(today);
    threeYearsAgo.setFullYear(yearThreeYearsAgo);

    if (today.getMonth() === 1 && today.getDate() === 29 && !isLeapYear(threeYearsAgo.getFullYear())) {
        threeYearsAgo.setMonth(1);
        threeYearsAgo.setDate(28);
    }

    const year = threeYearsAgo.getFullYear();
    const month = String(threeYearsAgo.getMonth() + 1).padStart(2, '0'); // I mesi sono 0-indexed
    const day = String(threeYearsAgo.getDate()).padStart(2, '0');

    const formattedDate = `${year}-${month}-${day}`;

    return formattedDate
}

export const isLeapYear = (year: any) => {
    return (year % 4 === 0 && year % 100 !== 0) || (year % 400 === 0);
}

/**
 * Corregge stringhe con problemi di encoding
 */
export function fixEncoding(str) {
    if (!str) return str;
    try {
        return decodeURIComponent(escape(str));
    } catch {
        return str;
    }
}